import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

import { OpportunityCreateModal } from './OpportunityCreateModal.jsx';

const STAGE_IDS = ['LEAD', 'QUALIFY', 'PROPOSAL', 'NEGOTIATE', 'CLOSED_WON', 'CLOSED_LOST'];

export function PipelinePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['sales', 'opportunities'],
    queryFn: () => salesApi.listOpportunities().then((r) => r.items || []),
  });

  const transition = useMutation({
    mutationFn: ({ id, stage }) => salesApi.transitionOpportunity(id, { stage }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales', 'opportunities'] }),
  });

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGE_IDS.map((id) => [id, []]));
    for (const opp of data || []) {
      if (map[opp.stage]) {
        map[opp.stage].push(opp);
      }
    }
    return map;
  }, [data]);

  if (isLoading) {
    return (
      <section className="page">
        <Spinner />
      </section>
    );
  }

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('sales.pipeline.title')}</h1>
          <p className="muted">{t('sales.pipeline.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('sales.pipeline.action.new')}
          </Button>
        </div>
      </header>
      {error && <div className="alert danger">{error.message}</div>}
      <div className="kanban">
        {STAGE_IDS.map((stageId) => {
          const items = byStage[stageId] || [];
          const total = items.reduce((acc, o) => acc + Number(o.amount || 0), 0);
          return (
            <div className="kanban-col" key={stageId}>
              <div className="kanban-col-head">
                <span>{t(`sales.pipeline.stage.${stageId}`)}</span>
                <span>
                  {items.length} · {formatCurrency(total)}
                </span>
              </div>
              <div className="kanban-col-body">
                {items.map((opp) => (
                  <article key={opp.id} className="opp-card">
                    <div className="title">{opp.title}</div>
                    <div className="meta">
                      <span>{formatCurrency(opp.amount, opp.currency)}</span>
                      <span>{opp.probability}%</span>
                    </div>
                    <div className="meta">
                      <span className="faint">{formatDate(opp.expectedCloseDate)}</span>
                    </div>
                    <select
                      className="select"
                      value={opp.stage}
                      onChange={(e) => transition.mutate({ id: opp.id, stage: e.target.value })}
                      disabled={transition.isPending}
                    >
                      {STAGE_IDS.map((id) => (
                        <option key={id} value={id}>
                          {t(`sales.pipeline.stage.${id}`)}
                        </option>
                      ))}
                    </select>
                  </article>
                ))}
                {items.length === 0 && <div className="faint text-xs">{t('empty.opps')}</div>}
              </div>
            </div>
          );
        })}
      </div>
      <OpportunityCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </section>
  );
}
